/**
 * Photo Color Layers tab: import an image (SVG inputs are rasterized), reduce
 * it to up to 30 MOPA color layers on a dot grid, edit per-layer cut params,
 * and export a layered SVG, palette CSV, printable report, or standalone
 * .lbrn2. Preview recomputes live; exports are gated on a current result.
 */
import type {
  CutParams,
  DotShape,
  LayerSortMode,
  PaletteMode,
  PhotoSettings,
} from "../../core/types";
import {
  button,
  checkboxField,
  clearNode,
  cutParamsEditor,
  dimField,
  dimSlider,
  el,
  numberField,
  photoExportReady,
  section,
  selectField,
  sliderField,
  swatch,
  textField,
  type PanelContext,
  type PanelHandle,
} from "../app";
import {
  DEFAULT_CUT_PARAMS,
  MATERIAL_PRESETS,
  PRESET_DISCLAIMER,
  nearestPresetParams,
} from "../presets";

const SHAPE_OPTS: { value: DotShape; label: string }[] = [
  { value: "square", label: "Square" },
  { value: "circle", label: "Circle" },
  { value: "diamond", label: "Diamond" },
  { value: "hexagon", label: "Hexagon" },
  { value: "octagon", label: "Octagon" },
  { value: "triangle", label: "Triangle" },
  { value: "star", label: "Star" },
  { value: "heart", label: "Heart" },
  { value: "cross", label: "Cross" },
];
const PALETTE_OPTS: { value: PaletteMode; label: string }[] = [
  { value: "adaptive", label: "Adaptive (image colors)" },
  { value: "lightburn", label: "LightBurn palette (exact)" },
];
const SORT_OPTS: { value: LayerSortMode; label: string }[] = [
  { value: "dark-to-light", label: "Dark → light" },
  { value: "light-to-dark", label: "Light → dark" },
  { value: "most-used", label: "Most used" },
  { value: "hue", label: "Hue" },
];

export function createPhotoPanel(ctx: PanelContext): PanelHandle {
  const { store, actions } = ctx;
  const root = el("div");
  const s0 = store.get();

  const updatePhoto = (patch: Partial<PhotoSettings>): void => {
    store.set((s) => ({ photo: { ...s.photo, ...patch }, photoResultSignature: null }));
    actions.persist();
    actions.schedulePhotoPreview();
  };

  // ---- import -------------------------------------------------------------
  const importSection = section("Image", { open: true });
  importSection.content.append(
    button("Import image…", { variant: "primary full", onClick: () => actions.browsePhotoImage() }),
    el("p", { class: "section-hint", text: "PNG, JPG, WEBP, GIF, BMP, or SVG. SVG is rasterized at 4× the output grid before sampling." }),
  );

  // ---- grid ---------------------------------------------------------------
  const gridSection = section("Grid & output", { open: true });
  gridSection.content.append(
    dimField(ctx, { label: "Output width", valueMm: s0.photo.outputWidthMm, minMm: 1, stepMm: 1, title: "Physical width of the finished mark.", onChangeMm: (v) => updatePhoto({ outputWidthMm: v }) }),
    dimSlider(ctx, { label: "Dot size", valueMm: s0.photo.pixelSize, minMm: 0.05, maxMm: 2, stepMm: 0.01, title: "Dot diameter / width. Color needs a filled area to read.", onChangeMm: (v) => updatePhoto({ pixelSize: v }) }),
    el("div", { class: "field-row" }, [
      dimField(ctx, { label: "Detail — across", valueMm: s0.photo.spacingX, minMm: 0.01, stepMm: 0.01, title: "Horizontal dot pitch (spacing between centers).", onChangeMm: (v) => updatePhoto({ spacingX: v }) }),
      dimField(ctx, { label: "Detail — down", valueMm: s0.photo.spacingY, minMm: 0.01, stepMm: 0.01, title: "Vertical dot pitch (spacing between centers).", onChangeMm: (v) => updatePhoto({ spacingY: v }) }),
    ]),
    selectField<DotShape>({ label: "Dot shape", value: s0.photo.shape, options: SHAPE_OPTS, onChange: (v) => updatePhoto({ shape: v }) }).root,
  );

  // ---- color --------------------------------------------------------------
  const colorSection = section("Color separation", { open: true });
  const startLayerField = numberField({ label: "First layer number", value: s0.photo.startLayer, min: 0, max: 29, step: 1, title: "First LightBurn cut-layer index to use (C00–C29).", onChange: (v) => updatePhoto({ startLayer: Math.round(v) }) });
  const syncStartLayer = (mode: PaletteMode): void => {
    startLayerField.root.style.display = mode === "lightburn" ? "" : "none";
  };
  colorSection.content.append(
    sliderField({ label: "Max color layers", value: s0.photo.maxLayers, min: 1, max: 30, step: 1, title: "LightBurn supports 30 cut layers (C00–C29).", onChange: (v) => updatePhoto({ maxLayers: Math.round(v) }) }).root,
    selectField<PaletteMode>({ label: "Palette mode", value: s0.photo.paletteMode, options: PALETTE_OPTS, onChange: (v) => { syncStartLayer(v); updatePhoto({ paletteMode: v }); } }).root,
    selectField<LayerSortMode>({ label: "Burn order", value: s0.photo.sortMode, options: SORT_OPTS, title: "Order layers are marked in. Dark → light first is gentler on adjacent oxide.", onChange: (v) => updatePhoto({ sortMode: v }) }).root,
    startLayerField.root,
    checkboxField({ label: "Smooth color blending (dither)", title: "Floyd–Steinberg dithering spreads quantization error to neighboring dots for smoother gradients.", checked: s0.photo.dither, onChange: (v) => updatePhoto({ dither: v }) }).root,
    checkboxField({ label: "Skip transparent areas", checked: s0.photo.ignoreTransparent, onChange: (v) => updatePhoto({ ignoreTransparent: v }) }).root,
    checkboxField({ label: "Skip near-white areas", title: "Leave near-white pixels unmarked (bare metal).", checked: s0.photo.ignoreNearWhite, onChange: (v) => updatePhoto({ ignoreNearWhite: v }) }).root,
    checkboxField({ label: "Combine touching squares", sub: "Square shape only; merges runs into fewer shapes.", checked: s0.photo.optimizeSquareRuns, onChange: (v) => updatePhoto({ optimizeSquareRuns: v }) }).root,
    numberField({ label: "Max pixels", value: s0.photo.maxPixels, min: 1, step: 10000, onChange: (v) => updatePhoto({ maxPixels: Math.round(v) }) }).root,
  );
  syncStartLayer(s0.photo.paletteMode);

  // ---- palette ------------------------------------------------------------
  const paletteSection = section("Palette & cut params", { open: true });
  const paletteList = el("div", { class: "palette-list" });
  const presetSelect = selectField({
    label: "Material preset",
    title: "Seeds starting cut parameters for a machine + material. Starting points only — calibrate before relying on them.",
    value: "0",
    options: MATERIAL_PRESETS.map((p, i) => ({ value: String(i), label: p.name })),
    onChange: () => {},
  });
  const applyBtn = button("Apply preset to all layers", { variant: "sm", onClick: () => applyPreset() });
  paletteSection.content.append(
    paletteList,
    presetSelect.root,
    el("div", { class: "btn-row" }, [applyBtn]),
    el("p", { class: "disclaimer", text: PRESET_DISCLAIMER }),
  );

  function applyPreset(): void {
    const s = store.get();
    if (!s.photoResult) return;
    const preset = MATERIAL_PRESETS[Number(presetSelect.get())] ?? MATERIAL_PRESETS[0];
    const cp: Record<number, CutParams> = { ...s.cutParams };
    for (const entry of s.photoResult.palette) {
      const p = nearestPresetParams(preset, entry.targetColor);
      if (p) cp[entry.layerIndex] = p;
    }
    store.set({ cutParams: cp });
    actions.persist();
    renderPalette();
    actions.toast("success", "Applied " + preset.name + " to all layers.");
  }

  function renderPalette(): void {
    clearNode(paletteList);
    const s = store.get();
    if (!s.photoResult) {
      paletteList.append(el("p", { class: "section-hint", text: "Generate to see the color palette." }));
      paletteSection.setBadge(null);
      return;
    }
    paletteSection.setBadge(String(s.photoResult.palette.length));
    for (const entry of s.photoResult.palette) {
      const code = "C" + String(entry.layerIndex).padStart(2, "0");
      const row = el("div", { class: "palette-row" });
      const paramsWrap = el("div", { class: "layer-params" });
      let built = false;
      const expandBtn = el("button", { class: "layer-expand", text: "params ▾" }) as HTMLButtonElement;
      expandBtn.addEventListener("click", () => {
        // Accordion: collapse any other open row before opening this one.
        if (!row.classList.contains("expanded")) {
          for (const other of paletteList.querySelectorAll<HTMLElement>(".palette-row.expanded")) {
            if (other === row) continue;
            other.classList.remove("expanded");
            const btn = other.querySelector<HTMLButtonElement>(".layer-expand");
            if (btn) btn.textContent = "params ▾";
          }
        }
        const open = row.classList.toggle("expanded");
        expandBtn.textContent = open ? "params ▴" : "params ▾";
        if (open && !built) {
          built = true;
          const stored = store.get().cutParams[entry.layerIndex];
          const initial = stored ?? nearestPresetParams(MATERIAL_PRESETS[0], entry.targetColor) ?? DEFAULT_CUT_PARAMS;
          if (!stored) {
            // Write the suggestion through so what the editor displays is
            // exactly what exports embed — never display-only values.
            store.set((st) => ({ cutParams: { ...st.cutParams, [entry.layerIndex]: initial } }));
            actions.persist();
          }
          paramsWrap.append(
            cutParamsEditor(initial, (p) => {
              store.set((st) => ({ cutParams: { ...st.cutParams, [entry.layerIndex]: p } }));
              actions.persist();
            }),
          );
        }
      });
      const head = el("div", { class: "palette-head" }, [
        swatch(entry.svgColor),
        el("span", { class: "palette-code", text: code }),
        el("span", { class: "layer-name", text: entry.targetColor, title: `image ${entry.targetColor} → layer ${entry.svgColor}` }),
        el("span", { class: "palette-pct", text: entry.percent.toFixed(1) + "%" }),
        expandBtn,
      ]);
      row.append(head, paramsWrap);
      paletteList.append(row);
    }
  }

  // ---- report meta --------------------------------------------------------
  const reportSection = section("Report header", { open: false });
  reportSection.content.append(
    textField({ label: "Machine", value: s0.reportMachine, onChange: (v) => store.set({ reportMachine: v }) }).root,
    textField({ label: "Material", value: s0.reportMaterial, onChange: (v) => store.set({ reportMaterial: v }) }).root,
    el("p", { class: "section-hint", text: "Printed at the top of the palette report." }),
  );

  // ---- actions ------------------------------------------------------------
  const actionsSection = section("Generate & export", { open: true });
  const genBtn = button("Generate / refresh preview", { variant: "primary full", onClick: () => actions.schedulePhotoPreview() });
  const gateNotice = el("div", { class: "notice", text: "Import an image to begin." });
  const svgBtn = button("Layered SVG", { onClick: () => actions.exportPhotoSvg() });
  const csvBtn = button("Palette CSV", { onClick: () => actions.exportPhotoCsv() });
  const reportBtn = button("Printable report", { onClick: () => actions.exportPhotoReport() });
  const lbrnBtn = button("Standalone .lbrn2", { onClick: () => actions.exportPhotoLbrn() });
  actionsSection.content.append(
    genBtn,
    gateNotice,
    el("div", { class: "btn-row" }, [svgBtn, lbrnBtn]),
    el("div", { class: "btn-row" }, [csvBtn, reportBtn]),
  );

  const exportButtons = [svgBtn, csvBtn, reportBtn, lbrnBtn];

  function updateGating(): void {
    const s = store.get();
    const ready = photoExportReady(s);
    const reason = !s.photoImage
      ? "Import an image first."
      : !s.photoResult
        ? "Generating…"
        : !ready
          ? "Settings changed — refresh the preview."
          : "";
    for (const b of exportButtons) {
      b.disabled = !ready;
      b.title = reason;
    }
    if (!s.photoImage) {
      gateNotice.className = "notice";
      gateNotice.textContent = "Import an image to begin.";
    } else if (ready) {
      gateNotice.className = "notice accent";
      gateNotice.textContent = "Preview current — exports enabled.";
    } else {
      gateNotice.className = "notice warn";
      gateNotice.textContent = reason;
    }
  }

  root.append(
    importSection.root,
    gridSection.root,
    colorSection.root,
    paletteSection.root,
    reportSection.root,
    actionsSection.root,
  );

  let lastResult = s0.photoResult;
  renderPalette();
  updateGating();
  store.subscribe(() => {
    const s = store.get();
    if (s.photoResult !== lastResult) {
      lastResult = s.photoResult;
      renderPalette();
    }
    updateGating();
  });

  return { element: root };
}
